import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import SubmitButton from './partials/SubmitButton';
import ErrorMessage from './partials/ErrorMessage';
import TextLink from './partials/TextLink';

import { HelpIcon, MSDIcon } from '../../misc/icons/Icons';
import Inputfield from '../../misc/Inputfield';
import useTranslation from '../../../hooks/useTranslations';
import { login, selectLoginError, selectLoginStatus } from '../../../redux/modules/authSlice';
import LinkedTranslation from '../../misc/LinkedTranslation';

const LoginScreen = () => {
	const t = useTranslation();
	const dispatch = useDispatch();

	const loginStatus = useSelector((state) => selectLoginStatus(state));
	const loginError = useSelector((state) => selectLoginError(state));

	const [email, setEmail] = useState(process.env.NODE_ENV === 'development' ? process.env.REACT_APP_USERNAME : '');
	const [password, setPassword] = useState(process.env.NODE_ENV === 'development' ? process.env.REACT_APP_PASSWORD : '');

	const [emailError, setEmailError] = useState(null);

	const validateFields = () => {
		setEmailError(null);

		if (!(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(String(email).toLowerCase()))) {
			setEmailError(t('register_validation_mail_not_valid'));
			return false;
		}
		setEmailError(null);
		return true;
	};


	const handleLogin = () => {
		if (validateFields()) {
			dispatch(login({ email, key: password }));
		}
	};

	const infoIcon = (
		<>
			<HelpIcon className="w-5 h-5" data-tip={t('webapp_activationkey_help')} data-place="right" />
			<ReactTooltip
				className="max-w-[240px]"
			/>
		</>
	);

	return (
		<div className="flex flex-col justify-center min-h-screen py-12 sm:pb-48 sm:px-6 lg:px-8">
			<div className="px-4 sm:mx-auto sm:w-full sm:max-w-md">
				<MSDIcon className="mx-auto w-42 h-14 sm:w-60 sm:h-20 text-oly-blue-dark" />
				<h2 className="mt-8 font-bold text-center text-[1.5625rem] text-oly-gray-darkest">
					{t('webapp_login_welcome_headline')}
				</h2>
				<h3 className="mt-2 font-bold text-center text-[1.0625rem] text-oly-gray-darkest">
					{t('webapp_login_welcome_text')}
				</h3>
				<p className="text-center mt-3 font-bold">
					<LinkedTranslation href="/register" text={t('webapp_login_register')} title={t('register_register_button')} colorLink="text-oly-blue-dark underline" />
				</p>
			</div>

			<div className="mx-4 mt-8 sm:mx-auto sm:w-full sm:max-w-md">
				<div className="p-2 bg-gray-200 mb-2 text-[10pt] rounded-sm shadow">
					Dear Olympus employees,
					<br />
					please log in using your @Olympus.com email address only. All old e-mail addresses or national e-mail addresses are no longer valid. If you have difficulties logging in, please use the &quot;Forgot Activation Key&quot; feature to reset your activation key. If the problem persists, please contact us at
					{' '}
					<TextLink external href="mailto:msd-mysalesdocs@olympus-europa.com" text="msd-mysalesdocs@olympus-europa.com" />
					.
				</div>
				<div className="px-4 py-8 bg-white rounded-sm shadow sm:px-10">
					<form action="#" method="POST" onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
						<div className="flex flex-col gap-y-4 sm:gap-y-6">
							<div className="w-full">
								<Inputfield fieldName="email" label={t('register_mail_placeholder')} type="text" initialValue={email} placeholder={t('forgot_text')} handleChange={setEmail} showLabel />
								{emailError && <ErrorMessage message={emailError} />}
							</div>

							<div className="w-full">
								<Inputfield fieldName="password" label={t('login_activationkey_placeholder')} type="password" initialValue={password} placeholder={t('webapp_login_key_placeholder')} handleChange={setPassword} showLabel afterLabelComponent={infoIcon} />
								{loginError && <ErrorMessage message={t(loginError)} />}
							</div>
						</div>

						<div className="flex items-center justify-between mx-1 mt-2">
							<TextLink href="/register" text={t('register_register_button')} title={t('register_register_button')} color="text-oly-blue-dark underline" />
							<TextLink href="/forgot-password" text={t('webapp_forgot_activationkey')} title={t('webapp_forgot_activationkey')} color="text-oly-blue-dark underline" />
						</div>

						<div className="flex items-center justify-between mx-1 mt-6">
							{process.env.REACT_APP_LINKS_IMPRINT && process.env.REACT_APP_LINKS_IMPRINT !== '' && (<TextLink href={process.env.REACT_APP_LINKS_IMPRINT} text={t('webapp_footer_imprint')} title={t('webapp_footer_imprint')} external color="text-oly-gray-darkest" />)}
							{process.env.REACT_APP_LINKS_PRIVACY && process.env.REACT_APP_LINKS_PRIVACY !== '' && (<TextLink href={process.env.REACT_APP_LINKS_PRIVACY} text={t('webapp_footer_privacy')} title={t('webapp_footer_privacy')} external color="text-oly-gray-darkest" />)}
						</div>

						<div className="mt-6">
							<SubmitButton type="submit" title={t('webapp_login_sign_in')} loading={loginStatus === 'pending'} disabled={email === '' || password === ''} handleClick={handleLogin} />
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default LoginScreen;
